import { pxToNumber } from '@doltech/utils/lib/dom';
import isHotkey from 'is-hotkey';
import { range, sum } from 'lodash';
import { nanoid } from 'nanoid';
import { Element } from 'slate';

import { createNode } from '../../../../slatePlugins/common/utils/createNode';
import {
  TableCellUpdate,
  TableClassNames,
  TableElement,
  TableOption,
  TableSizeConstant,
  TableType,
} from './types';

const ELEMENT_ALIGN_LEFT = 'align_left';

export const isThRow = (element: Element) => {
  if (element && TableElement.TR === element.type && element.children.length > 0) {
    // a row would not mix between TH & TD so we need to check first child only
    const lastChild = element.children[element.children.length - 1]; // NOTE: Cannot use assumption first child is TH, then row is TH row as we have column heading should have first item in row is TH
    if (TableElement.TH === lastChild.type) {
      return true;
    }
  }

  return false;
};

/**
 * type should be one of ELEMENT_ALIGN_LEFT, ELEMENT_ALIGN_RIGHT, ELEMENT_ALIGN_CENTER
 */
export const generateCellWithAlignment = (type: string = ELEMENT_ALIGN_LEFT) => ({
  type,
  children: [createNode()],
});

export const createTh = (opt: TableCellUpdate = {}) => ({
  id: nanoid(),
  type: TableElement.TH,

  scope: 'col',

  children: [generateCellWithAlignment()],
  ...opt,
});

export const createTd = (opt: TableCellUpdate = {}) => ({
  id: nanoid(),
  type: TableElement.TD,
  children: [generateCellWithAlignment()],
  ...opt,
});

export const createTr = (
  cols: number,
  type?: TableElement.TD | TableElement.TH,
  opt?: TableCellUpdate
) => ({
  type: TableElement.TR,
  id: nanoid(),
  isExpandable: false,
  isCollapsed: false,
  children: range(cols).map(() => (type === TableElement.TH ? createTh(opt) : createTd(opt))),
});

export const newTrWithColHeading = (cols: number, opt?: TableCellUpdate) => ({
  type: TableElement.TR,
  id: nanoid(),
  children: range(cols).map((_, index) =>
    index === 0 ? createTh({ ...opt, scope: 'row' }) : createTd(opt)
  ),
});

const DEFAUT_TABLE_OPTION = { rows: 2 };

/** Bounding width always between predefined minWidth & maxWidth */
export const limitCellWidth = (width: number) =>
  Math.min(
    Math.max(TableSizeConstant.TABLE_CELL_MIN_WIDTH, width),
    TableSizeConstant.TABLE_CELL_MAX_WIDTH
  );

export const createTable = (
  tableProperties: Partial<TableType> = {},
  options: TableOption = {}
) => {
  const cellsWidth = tableProperties?.cellsWidth?.map(limitCellWidth) ?? [
    TableSizeConstant.TABLE_CELL_MAX_WIDTH,
    TableSizeConstant.TABLE_CELL_MAX_WIDTH,
  ];
  const { rows } = { ...DEFAUT_TABLE_OPTION, ...options };
  delete tableProperties.width;
  return {
    type: TableElement.TABLE,
    border: true,
    id: nanoid(),
    cellsWidth,
    width: sum(cellsWidth),
    hasRowHeading: true,
    hasColumnHeading: false,
    children: [
      createTr(tableProperties?.cellsWidth?.length ?? 2, TableElement.TH),
      ...range(rows - 1).map(() =>
        createTr(tableProperties?.cellsWidth?.length ?? 2, TableElement.TD)
      ),
    ],
    ...tableProperties,
  } as TableType;
};

export const matchTableContent = (node: any) =>
  [TableElement.TD, TableElement.TH].includes(node?.type);

export const pixelsToNumbers = (pxs: string[] | number[]) => pxs.map(pxToNumber);

export const numberToPixels = (numbers: number[]) => numbers.map((num) => `${num}px`);

export const isForward = isHotkey(['tab']);
export const isBackward = isHotkey(['shift+tab']);

/**
 * options: {
 *   joiner: string, // put into join(joiner)
 *   prefix:string, // put before .class. Ex: prefix = &, className = 'hey' -> &.hey
 * }
 */
export const toCssSelector = (
  classNames: TableClassNames | TableClassNames[] | string | string[],
  options?: { joiner?: string; prefix?: string }
) => {
  const { joiner, prefix } = options ?? {};
  return (Array.isArray(classNames) ? classNames : [classNames])
    .map((className) => `${prefix ?? ''}.${className}`)
    .join(joiner ?? '');
};
